// client/src/components/clientes/ClienteForm.js - CORREÇÃO DO CAMPO OBSERVAÇÕES
import React, { useState, useEffect, useCallback } from 'react';
import { useMutateClientes } from '../../hooks/useClientes';
import useAlert from '../../hooks/useAlert';
import { Save, XCircle, Loader2, Camera, CalendarDays, User as UserIcon, AlertCircle, Tag, Plus, Minus, CheckCircle2, Gift } from 'lucide-react';
import FotoPerfilUploader from './uploads/FotoPerfilUploader';
import HistoricoAgendamentos from './HistoricoAgendamentos';
import FidelidadeCliente from './FidelidadeCliente';
import { lookupCep } from '../../utils/cepService';
import { useMe } from '../../hooks/useAuth';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

// "Agendamentos" é um histórico somente-leitura, não uma seção do
// formulário — fica fora do wizard de Dados/Endereço, acessível por um
// alternador próprio (só existe no modo edição).
const STEP_LABELS = ['Dados Gerais', 'Endereço'];

const formatCPF = (value) => {
    if (!value) return "";
    const cpf = String(value).replace(/\D/g, "");
    if (cpf.length <= 11) {
        return cpf.replace(/(\d{3})(\d)/, "$1.$2")
                  .replace(/(\d{3})(\d)/, "$1.$2")
                  .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return value.substring(0, 14);
};

const formatTelefone = (value) => {
    if (!value) return "";
    const telefone = String(value).replace(/\D/g, "");
    if (telefone.length === 0) return "";
    if (telefone.length <= 2) return `(${telefone}`;
    if (telefone.length <= 6) return `(${telefone.slice(0,2)}) ${telefone.slice(2)}`;
    if (telefone.length <= 10) return `(${telefone.slice(0,2)}) ${telefone.slice(2,6)}-${telefone.slice(6)}`;
    if (telefone.length <= 11) return `(${telefone.slice(0,2)}) ${telefone.slice(2,7)}-${telefone.slice(7,11)}`;
    return value.substring(0,15);
};

const formatCEP = (value) => {
    if (!value) return "";
    const cep = String(value).replace(/\D/g, "");
    if (cep.length <= 8) {
        return cep.replace(/(\d{5})(\d)/, "$1-$2");
    }
    return value.substring(0, 9);
};

const initialState = {
    _id: null,
    nome: '',
    nomeCompleto: '',
    email: '',
    telefone: '',
    cpf: '',
    dataNascimento: '',
    endereco: { 
        cep: '', 
        logradouro: '', 
        numero: '', 
        complemento: '', 
        bairro: '', 
        localidade: '', 
        uf: '' 
    },
    tags: [],
    observacoesCliente: '', // ✅ MANTER O NOME ORIGINAL DO MODELO
    ativo: true
};

// ✅ CORREÇÃO: Função para limpar e preparar dados antes do envio
const prepareDataForSubmission = (data) => {
    const cleaned = { ...data };
    
    // 1. Limpar e formatar campos específicos
    if (cleaned.nome) {
        cleaned.nome = String(cleaned.nome).trim();
    } else if (cleaned.nomeCompleto) {
        cleaned.nome = String(cleaned.nomeCompleto).trim();
        delete cleaned.nomeCompleto;
    }
    
    if (cleaned.nome === '' || cleaned.nome === 'null') {
        delete cleaned.nome;
    }
    
    // ✅ CORREÇÃO: Email
    if (cleaned.email) {
        cleaned.email = String(cleaned.email).trim();
        if (cleaned.email === '' || cleaned.email === 'null') {
            delete cleaned.email;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned.email)) {
            delete cleaned.email;
        }
    } else {
        delete cleaned.email;
    }
    
    // ✅ CORREÇÃO: Telefone
    if (cleaned.telefone) {
        cleaned.telefone = String(cleaned.telefone).replace(/\D/g, '');
        if (cleaned.telefone === '') {
            delete cleaned.telefone;
        }
    } else if (cleaned.telefonePrincipal) {
        cleaned.telefone = String(cleaned.telefonePrincipal).replace(/\D/g, '');
        if (cleaned.telefone === '') {
            delete cleaned.telefone;
        }
        delete cleaned.telefonePrincipal;
    }
    
    // ✅ CPF
    if (cleaned.cpf) {
        cleaned.cpf = String(cleaned.cpf).replace(/\D/g, '');
        if (cleaned.cpf === '') {
            delete cleaned.cpf;
        }
    } else {
        delete cleaned.cpf;
    }
    
    // ✅ Data de nascimento
    if (!cleaned.dataNascimento || cleaned.dataNascimento === '' || cleaned.dataNascimento === 'null') {
        delete cleaned.dataNascimento;
    }
    
    // ✅ CORREÇÃO CRÍTICA: observacoesCliente - MANTER O NOME ORIGINAL
    if (cleaned.observacoesCliente !== undefined) {
        if (cleaned.observacoesCliente && typeof cleaned.observacoesCliente === 'string') {
            cleaned.observacoesCliente = cleaned.observacoesCliente.trim();
            if (cleaned.observacoesCliente === '') {
                delete cleaned.observacoesCliente;
            }
        } else {
            delete cleaned.observacoesCliente;
        }
    }
    
    // ✅ NÃO converter observacoesCliente para descricao
    // O modelo Cliente usa observacoesCliente, não descricao
    
    // ✅ Ativo
    if (cleaned.ativo === undefined && cleaned.status) {
        cleaned.ativo = cleaned.status === 'ativo';
        delete cleaned.status;
    }
    
    // ✅ Endereço
    if (cleaned.enderecoPessoal) {
        const cleanEndereco = {};
        Object.entries(cleaned.enderecoPessoal).forEach(([key, value]) => {
            if (value && String(value).trim() !== '') {
                cleanEndereco[key] = String(value).trim();
            }
        });
        
        if (Object.keys(cleanEndereco).length > 0) {
            cleaned.enderecoPessoal = cleanEndereco;
        } else {
            delete cleaned.enderecoPessoal;
        }
    } else if (cleaned.endereco && cleaned.endereco.cep) {
        const { cep, logradouro, numero, complemento, bairro, localidade, uf } = cleaned.endereco;
        const enderecoData = { cep, logradouro, numero, complemento, bairro, localidade, uf };
        const cleanEndereco = {};
        
        Object.entries(enderecoData).forEach(([key, value]) => {
            if (value && String(value).trim() !== '') {
                cleanEndereco[key] = String(value).trim();
            }
        });
        
        if (Object.keys(cleanEndereco).length > 0) {
            cleaned.enderecoPessoal = cleanEndereco;
        }
        delete cleaned.endereco;
    }
    
    // ✅ Tags
    if (!cleaned.tags || !Array.isArray(cleaned.tags) || cleaned.tags.length === 0) {
        delete cleaned.tags;
    } else {
        cleaned.tags = cleaned.tags
            .filter(tag => tag && typeof tag === 'string' && tag.trim() !== '')
            .map(tag => tag.trim());
        if (cleaned.tags.length === 0) {
            delete cleaned.tags;
        }
    }
    
    // ✅ Remover campos undefined ou null
    Object.keys(cleaned).forEach(key => {
        if (cleaned[key] === undefined || cleaned[key] === null) {
            delete cleaned[key];
        }
    });
    
    console.log('🧹 [prepareDataForSubmission] Dados limpos:', cleaned);
    return cleaned;
};

const ClienteForm = ({ onCloseForm, onSaveSuccess, current, showHeader = true, initialData }) => {
    const { createCliente, updateCliente } = useMutateClientes();
    const { setAlert } = useAlert();
    const { accountId } = useMe();
    
    const [formData, setFormData] = useState(initialState);
    const [view, setView] = useState('form');
    const [currentStep, setCurrentStep] = useState(1);
    const [isCepLoading, setIsCepLoading] = useState(false);
    const [cepEncontrado, setCepEncontrado] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [novaTag, setNovaTag] = useState('');
    
    const isEditing = !!current;
    const loading = createCliente.isPending || updateCliente.isPending;

    // ✅ CORREÇÃO: useEffect para inicializar dados
    useEffect(() => {
        if (isEditing && current) {
            console.log('✏️ [ClienteForm] Inicializando modo EDIÇÃO:', current._id);
            console.log('📝 [ClienteForm] Dados do current:', current);
            
            const userData = current.user || {};
            
            // Processar tags (garantir array)
            const tagsProcessadas = Array.isArray(current.tags) 
                ? current.tags.filter(tag => tag && typeof tag === 'string')
                : [];
            
            // ✅ CORREÇÃO: Verificar onde estão as observações
            let observacoes = '';
            if (current.observacoesCliente) {
                observacoes = current.observacoesCliente;
            } else if (current.descricao) {
                observacoes = current.descricao;
            } else if (current.observacoes) {
                observacoes = current.observacoes;
            }
            
            console.log('📝 [ClienteForm] Observações carregadas:', observacoes);
            
            setFormData({
                _id: current._id,
                nome: current.nome || current.nomeCompleto || userData.name || '',
                nomeCompleto: current.nome || current.nomeCompleto || userData.name || '',
                email: current.email || current.emailPrincipal || userData.email || '',
                telefone: current.telefone || current.telefonePrincipal || userData.telefone || '',
                cpf: current.cpf || userData.cpf || '',
                dataNascimento: userData.dataNascimento ? new Date(userData.dataNascimento).toISOString().split('T')[0] : '',
                endereco: current.enderecoPessoal || current.endereco || initialState.endereco,
                tags: tagsProcessadas,
                observacoesCliente: observacoes, // ✅ Usar observacoesCliente
                ativo: current.ativo !== undefined ? current.ativo : true
            });
        } else if (initialData) {
            // Prefill vindo da busca de cliente do agendamento (SearchableSelect
            // "Adicionar '{termo}' como novo") — o termo digitado (nome,
            // telefone ou email) já chega classificado em
            // ServicoAgendamentoForm.js/RecursoAgendamentoForm.js
            // (handleOpenNewClientModal).
            setFormData({
                ...initialState,
                nome: initialData.nomeCompleto || initialData.nome || '',
                nomeCompleto: initialData.nomeCompleto || initialData.nome || '',
                telefone: initialData.telefonePrincipal || initialData.telefone || '',
                email: initialData.emailPrincipal || initialData.email || '',
            });
        } else {
            setFormData(initialState);
        }
    }, [current, isEditing, initialData]);

    const validateForm = () => {
        const errors = {};
        
        if (!formData.nome || !formData.nome.trim()) {
            errors.nome = 'O nome é obrigatório';
        }
        
        if (!formData.telefone || !String(formData.telefone).replace(/\D/g, '')) {
            errors.telefone = 'O telefone é obrigatório';
        } else if (String(formData.telefone).replace(/\D/g, '').length < 10) {
            errors.telefone = 'Telefone inválido (mínimo 10 dígitos)';
        }
        
        if (formData.email && formData.email.trim()) {
            const email = formData.email.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                errors.email = 'Por favor, informe um email válido';
            }
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const onChange = useCallback(e => {
        const { name, value } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: value 
        }));
        
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    }, [formErrors]);

    const onEnderecoChange = useCallback(e => {
        const { name, value } = e.target;
        if (name === 'cep') setCepEncontrado(false);
        setFormData(prev => ({
            ...prev,
            endereco: { ...prev.endereco, [name]: value }
        }));
    }, []);

    const adicionarTag = useCallback(() => {
        if (novaTag.trim() && !formData.tags.includes(novaTag.trim())) {
            setFormData(prev => ({ 
                ...prev, 
                tags: [...prev.tags, novaTag.trim()] 
            }));
            setNovaTag('');
        }
    }, [novaTag, formData.tags]);

    const removerTag = useCallback((tagParaRemover) => {
        setFormData(prev => ({ 
            ...prev, 
            tags: prev.tags.filter(tag => tag !== tagParaRemover) 
        }));
    }, []);

    const handleTagKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            adicionarTag();
        }
    }, [adicionarTag]);
    
    const onCepBlur = useCallback(async (e) => {
        const cepValue = e.target.value.replace(/\D/g, '');
        if (cepValue.length !== 8) return;
        
        setIsCepLoading(true);
        try {
            const enderecoData = await lookupCep(cepValue);
            setFormData(prev => ({ 
                ...prev, 
                endereco: { 
                    ...prev.endereco,
                    cep: enderecoData.cep,
                    logradouro: enderecoData.logradouro,
                    bairro: enderecoData.bairro,
                    localidade: enderecoData.localidade,
                    uf: enderecoData.uf
                }
            }));
            setCepEncontrado(true);
            setAlert('CEP encontrado com sucesso!', 'success');
        } catch (cepError) {
            console.error("Erro ao buscar CEP:", cepError);
            setAlert(cepError.message || 'Erro ao consultar o CEP.', 'danger');
        } finally { 
            setIsCepLoading(false); 
        }
    }, [setAlert]);
    
    const onSubmit = e => {
        e.preventDefault();
        
        if (!validateForm()) {
            setAlert('Por favor, corrija os erros no formulário', 'warning');
            return;
        }

        // ✅ Preparar dados para envio - MANTER observacoesCliente
        const dataToSubmit = prepareDataForSubmission({
            _id: formData._id,
            nome: formData.nome.trim(),
            email: formData.email?.trim() || undefined,
            telefone: String(formData.telefone).replace(/\D/g, ''),
            cpf: String(formData.cpf || '').replace(/\D/g, '') || undefined,
            dataNascimento: formData.dataNascimento || null,
            enderecoPessoal: formData.endereco,
            tags: formData.tags,
            observacoesCliente: formData.observacoesCliente?.trim() || undefined, // ✅ MANTER O NOME CORRETO
            ativo: formData.ativo
        });

        if (!isEditing && accountId) {
            dataToSubmit.account = accountId;
        }

        // ✅ useMutateClientes (createCliente/updateCliente) já dispara
        // setAlert de sucesso/erro — inclusive já traduz "Email já
        // cadastrado"/"Telefone já cadastrado" pra mensagem amigável dentro
        // do mutationFn. Repetir setAlert aqui duplicava o toast pro
        // usuário; só onSaveSuccess() (fecha/navega o form) é próprio deste
        // componente.
        const mutationOptions = {
            onSuccess: (response) => {
                onSaveSuccess(response);
            }
        };

        if (isEditing) {
            updateCliente.mutate({ id: formData._id, clienteData: dataToSubmit }, mutationOptions);
        } else {
            createCliente.mutate(dataToSubmit, mutationOptions);
        }
    };
    
    return (
        <form onSubmit={onSubmit} noValidate>
            {showHeader && ( 
                <div className="form-header"> 
                    <h3 className="form-title">
                        {isEditing ? `Editar Cliente: ${formData.nome || '...'}` : 'Adicionar Novo Cliente'}
                    </h3> 
                    <button type="button" onClick={onCloseForm} title="Fechar Formulário" className="form-close-button">
                        <XCircle size={24} />
                    </button> 
                </div> 
            )}
            
            {isEditing && (
                <div className="form-tabs-nav">
                    <button type="button" className={`tab-button ${view === 'form' ? 'is-active' : ''}`} onClick={() => setView('form')}>
                        <UserIcon size={16}/> Editar Dados
                    </button>
                    <button type="button" className={`tab-button ${view === 'agendamentos' ? 'is-active' : ''}`} onClick={() => setView('agendamentos')}>
                        <CalendarDays size={16}/> Agendamentos
                    </button>
                    <button type="button" className={`tab-button ${view === 'fidelidade' ? 'is-active' : ''}`} onClick={() => setView('fidelidade')}>
                        <Gift size={16}/> Fidelidade
                    </button>
                </div>
            )}

            {view === 'form' && <FormStepper currentStep={currentStep} steps={STEP_LABELS} />}

            <div className="form-tabs-content">
                {view === 'form' && currentStep === 1 && (
                    <div className="form-grid-container-cols-2"> 
                        <div> 
                            <div className="form-section"> 
                                <h4 className="form-section-title">Dados Pessoais</h4> 
                                <div className="form-group">
                                    <label htmlFor="nome" className="required">Nome Completo:</label>
                                    <input 
                                        type="text" 
                                        name="nome" 
                                        id="nome" 
                                        value={formData.nome || ''} 
                                        onChange={onChange} 
                                        required 
                                        className={`form-input ${formErrors.nome ? 'input-error' : ''}`} 
                                        placeholder="Digite o nome completo"
                                    />
                                    {formErrors.nome && (
                                        <div className="form-error-message">
                                            <AlertCircle size={14} />
                                            {formErrors.nome}
                                        </div>
                                    )}
                                </div> 
                                <div className="form-group">
                                    <label htmlFor="cpf">CPF:</label>
                                    <input 
                                        type="text" 
                                        name="cpf" 
                                        id="cpf" 
                                        value={formatCPF(formData.cpf || '')} 
                                        onChange={onChange} 
                                        className="form-input" 
                                        placeholder="000.000.000-00" 
                                        maxLength="14"
                                    />
                                    <div className="form-hint">
                                        Apenas números (opcional)
                                    </div>
                                </div> 
                                <div className="form-group">
                                    <label htmlFor="dataNascimento">Data de Nascimento:</label>
                                    <input 
                                        type="date" 
                                        name="dataNascimento" 
                                        id="dataNascimento" 
                                        value={formData.dataNascimento || ''} 
                                        onChange={onChange} 
                                        className="form-input" 
                                    />
                                </div> 
                            </div> 
                            <div className="form-section"> 
                                <h4 className="form-section-title">Contato</h4> 
                                <div className="form-grid-container"> 
                                    <div className="form-group">
                                        <label htmlFor="email">Email Principal:</label>
                                        <input 
                                            type="email" 
                                            name="email" 
                                            id="email"
                                            value={formData.email || ''} 
                                            onChange={onChange} 
                                            className={`form-input ${formErrors.email ? 'input-error' : ''}`} 
                                            placeholder="email@exemplo.com (opcional)"
                                        />
                                        {formErrors.email && (
                                            <div className="form-error-message">
                                                <AlertCircle size={14} />
                                                {formErrors.email}
                                            </div>
                                        )}
                                        <div className="form-hint">
                                            Deixe em branco se não tiver email
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="telefone" className="required">Telefone Principal:</label>
                                        <input 
                                            type="text" 
                                            name="telefone" 
                                            id="telefone"
                                            value={formatTelefone(formData.telefone || '')} 
                                            onChange={onChange} 
                                            required 
                                            className={`form-input ${formErrors.telefone ? 'input-error' : ''}`} 
                                            placeholder="(XX) XXXXX-XXXX" 
                                            maxLength="15"
                                        />
                                        {formErrors.telefone && (
                                            <div className="form-error-message">
                                                <AlertCircle size={14} />
                                                {formErrors.telefone}
                                            </div>
                                        )}
                                        <div className="form-hint">
                                            Apenas números (obrigatório)
                                        </div>
                                    </div> 
                                </div> 
                            </div> 
                        </div> 
                        <div> 
                            {isEditing && ( 
                                <div className="form-section"> 
                                    <h4 className="form-section-title">
                                        <Camera size={16} className="inline mr-2"/> 
                                        Foto de Perfil
                                    </h4> 
                                    <FotoPerfilUploader cliente={current} /> 
                                </div> 
                            )} 
                            <div className="form-section"> 
                                <h4 className="form-section-title">
                                    <Tag size={16} className="inline mr-2"/>
                                    Tags e Observações
                                </h4> 
                                
                                {/* TAGS */}
                                <div className="form-group full-width">
                                    <label htmlFor="tags">Tags</label>
                                    <div className="flex gap-2 mb-2">
                                        <input 
                                            type="text" 
                                            value={novaTag} 
                                            onChange={(e) => setNovaTag(e.target.value)} 
                                            onKeyPress={handleTagKeyPress} 
                                            className="form-input flex-1" 
                                            placeholder="Adicionar tag..." 
                                        />
                                        <button 
                                            type="button" 
                                            onClick={adicionarTag} 
                                            className="action-button action-button-secondary" 
                                            disabled={!novaTag.trim()}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {formData.tags.map((tag, index) => (
                                            <span key={index} className="tag">
                                                {tag}
                                                <button 
                                                    type="button" 
                                                    onClick={() => removerTag(tag)} 
                                                    className="tag-remove"
                                                >
                                                    <Minus size={12} />
                                                </button>
                                            </span>
                                        ))}
                                        {formData.tags.length === 0 && (
                                            <span className="text-sm text-gray-500">
                                                Nenhuma tag adicionada
                                            </span>
                                        )}
                                    </div>
                                </div>
                                
                                {/* ✅ CORREÇÃO: Campo observacoesCliente com o nome correto */}
                                <div className="form-group">
                                    <label htmlFor="observacoesCliente">Observações</label>
                                    <textarea 
                                        name="observacoesCliente" 
                                        id="observacoesCliente" 
                                        value={formData.observacoesCliente || ''} 
                                        onChange={onChange} 
                                        className="form-input" 
                                        rows={isEditing ? 5 : 8} 
                                        placeholder="Observações importantes sobre o cliente..."
                                    />
                                    <div className="form-hint">
                                        Campo opcional
                                    </div>
                                </div> 
                            </div> 
                        </div> 
                    </div> 
                )}
                
                {view === 'form' && currentStep === 2 && (
                    <div className="form-section"> 
                        <h4 className="form-section-title">Endereço do Cliente</h4> 
                        <div className="form-grid-container"> 
                            <div className="form-group"> 
                                <label htmlFor="cep">CEP:</label> 
                                <div className="input-with-loader"> 
                                    <input 
                                        type="text" 
                                        name="cep" 
                                        id="cep" 
                                        placeholder="00000-000" 
                                        value={formatCEP(formData.endereco?.cep || '')} 
                                        onChange={onEnderecoChange} 
                                        onBlur={onCepBlur} 
                                        className="form-input" 
                                        maxLength="9"
                                    /> 
                                    {isCepLoading && <Loader2 size={16} className="loader-icon animate-spin" />} 
                                </div>
                                {cepEncontrado ? (
                                    <div className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <CheckCircle2 size={13} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                                        Endereço preenchido automaticamente.
                                        <button type="button" onClick={() => setCepEncontrado(false)}
                                            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                                            Editar manualmente
                                        </button>
                                    </div>
                                ) : (
                                    <div className="form-hint">Digite o CEP e aguarde o preenchimento automático</div>
                                )}
                            </div>
                            <div className="form-group">
                                <label htmlFor="logradouro">Logradouro:</label>
                                <input
                                    type="text"
                                    name="logradouro"
                                    id="logradouro"
                                    value={formData.endereco?.logradouro || ''}
                                    onChange={onEnderecoChange}
                                    className="form-input"
                                    placeholder="Rua, Avenida, etc."
                                    readOnly={cepEncontrado}
                                    style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="numero">Número:</label>
                                <input 
                                    type="text" 
                                    name="numero" 
                                    id="numero" 
                                    value={formData.endereco?.numero || ''} 
                                    onChange={onEnderecoChange} 
                                    className="form-input" 
                                    placeholder="Número"
                                />
                            </div> 
                            <div className="form-group">
                                <label htmlFor="complemento">Complemento:</label>
                                <input 
                                    type="text" 
                                    name="complemento" 
                                    id="complemento" 
                                    value={formData.endereco?.complemento || ''} 
                                    onChange={onEnderecoChange} 
                                    className="form-input" 
                                    placeholder="Apartamento, Bloco, etc."
                                />
                            </div> 
                            <div className="form-group">
                                <label htmlFor="bairro">Bairro:</label>
                                <input
                                    type="text"
                                    name="bairro"
                                    id="bairro"
                                    value={formData.endereco?.bairro || ''}
                                    onChange={onEnderecoChange}
                                    className="form-input"
                                    placeholder="Bairro"
                                    readOnly={cepEncontrado}
                                    style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="localidade">Cidade:</label>
                                <input
                                    type="text"
                                    name="localidade"
                                    id="localidade"
                                    value={formData.endereco?.localidade || ''}
                                    onChange={onEnderecoChange}
                                    className="form-input"
                                    placeholder="Cidade"
                                    readOnly={cepEncontrado}
                                    style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="uf">UF:</label>
                                <input
                                    type="text"
                                    name="uf"
                                    id="uf"
                                    value={formData.endereco?.uf || ''}
                                    onChange={onEnderecoChange}
                                    maxLength="2"
                                    className="form-input"
                                    placeholder="UF"
                                    readOnly={cepEncontrado}
                                    style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                                />
                            </div> 
                        </div> 
                        <div className="form-hint mt-4 p-3 bg-gray-50 rounded">
                            <AlertCircle size={14} className="inline mr-1" />
                            Todos os campos de endereço são opcionais.
                        </div>
                    </div> 
                )}
                
                {view === 'agendamentos' && isEditing && (
                    <div className="form-section">
                        <h4 className="form-section-title">Histórico de Agendamentos</h4>
                        <HistoricoAgendamentos clienteId={formData._id} />
                    </div>
                )}

                {view === 'fidelidade' && isEditing && (
                    <div className="form-section">
                        <h4 className="form-section-title">Programa de Fidelidade</h4>
                        <FidelidadeCliente clienteId={formData._id} />
                    </div>
                )}
            </div>
            
            {showHeader && (
                <div className="form-actions">
                    <button type="button" onClick={onCloseForm} className="action-button action-button-secondary">
                        <XCircle size={18} /> Fechar
                    </button>
                    {view === 'form' ? (
                        <FormStepNavigation
                            currentStep={currentStep}
                            totalSteps={STEP_LABELS.length}
                            onBack={() => setCurrentStep((s) => s - 1)}
                            onNext={() => setCurrentStep((s) => s + 1)}
                            isSubmitting={loading || isCepLoading}
                            submitLabel={isEditing ? 'Salvar Alterações' : 'Criar Cliente'}
                        />
                    ) : (
                        <button type="button" onClick={() => setView('form')} className="action-button action-button-primary">
                            <Save size={18} /> Voltar para edição
                        </button>
                    )}
                </div>
            )}
        </form>
    );
};

export default ClienteForm;